import { excessSettlementFundsClaimMachine } from '@artblocks/sdk/dist/machines/excess-settlement-funds-claim-machine'
import { useSelector } from '@xstate/react'
import { formatEther } from 'viem'
import { useExcessSettlementFundsClaimMachineRefs } from '../excess-settlement-funds-manager-context/hooks'
import { ActorRefFrom } from '@artblocks/sdk/dist/machines/utils'
import * as Dialog from '@radix-ui/react-dialog'
import { AnimatePresence, motion } from 'motion/react'
import { cn } from '@/lib/utils'
import { BodyText, CheckIcon, CloseIcon, DetailText } from 'design-configuration/components'
import { Button } from '@components/button'
import { ImageWithCustomLoader } from '@components/image-with-custom-loader'
import { LoadingIndicator } from '@components/loading-indicator'
import { getVerticalTitle } from '@utils/project'
import { useEffect } from 'react'

/**
 * Renders a modal for claiming excess settlement funds from projects where the
 * clearing price was lower than the current user's purchase price.
 *
 * Displays a list of eligible claims, allowing users to review details and
 * initiate the claim process for each. Leverages the
 * `ExcessSettlementFundsManagerMachineContext` to manage state and optimize
 * re-renders based on the set of claim IDs.
 *
 * @param props The component props.
 * @param props.show Determines if the modal is visible.
 * @param props.onClose Callback function for when the modal is requested to be
 * closed.
 */
export const ExcessSettlementFundsClaimModal = ({ show, onClose }: { show: boolean; onClose: () => void }) => {
  const excessSettlementFundClaimMachineRefs = useExcessSettlementFundsClaimMachineRefs()

  const claimMachineEntries = [...(excessSettlementFundClaimMachineRefs?.entries() ?? [])]

  // Automatically close the modal if there are no claims to show
  useEffect(() => {
    if (show && claimMachineEntries.length === 0) {
      const timeout = setTimeout(() => {
        onClose()
      }, 1000)

      return () => clearTimeout(timeout)
    }
  }, [show, claimMachineEntries.length, onClose])

  return (
    <Dialog.Root open={show} onOpenChange={onClose}>
      <AnimatePresence>
        {show ? (
          <Dialog.Portal forceMount>
            <Dialog.Overlay asChild>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className={cn('bg-surface/50 fixed inset-0 z-40 backdrop-blur-sm')}
              />
            </Dialog.Overlay>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-50 flex items-center justify-center"
            >
              <Dialog.Content asChild>
                <div className="p-sm bg-surface rounded-xs border-outline-variant relative max-w-screen-sm border">
                  <div className="mb-md">
                    <Dialog.Title asChild>
                      <BodyText className="mb-xs font-medium">Claim settlements to your wallet</BodyText>
                    </Dialog.Title>
                    <BodyText>
                      For one or more projects from which you&apos;ve purchased tokens, the clearing price was lower
                      than your purchase price, entitling you to claim the difference.
                    </BodyText>
                  </div>
                  <Dialog.Close asChild>
                    <button className="right-sm top-sm absolute">
                      <CloseIcon className="w-sm h-sm" />
                    </button>
                  </Dialog.Close>
                  {claimMachineEntries.length > 0 ? (
                    <div className="border-outline-variant border">
                      {claimMachineEntries.map(([receiptId, claimMachine]) => {
                        return <ExcessSettlementFundClaimItem key={receiptId} claimMachine={claimMachine} />
                      })}
                    </div>
                  ) : null}
                </div>
              </Dialog.Content>
            </motion.div>
          </Dialog.Portal>
        ) : null}
      </AnimatePresence>
    </Dialog.Root>
  )
}

/**
 * Renders an individual claim item within the Excess Settlement Funds Claim
 * Modal.
 *
 * This component displays the details of a specific claim, including the
 * project name, the clearing price, and the amount claimable in ETH. It also
 * provides a button to initiate the claim process, with dynamic state updates
 * based on the claim's progress (idle, in progress, success, or error).
 *
 * The component leverages the `useSelector` hook from XState to subscribe to
 * state changes in the provided `claimMachine`, ensuring it re-renders only
 * when necessary to reflect the current state of the claim process.
 *
 * @param props The component props.
 * @param props.claimMachine An ActorRef from the
 * `excessSettlementFundsClaimMachine`, representing the state machine instance
 * for managing the claim process for a specific claim.
 */
function ExcessSettlementFundClaimItem({
  claimMachine
}: {
  claimMachine: ActorRefFrom<typeof excessSettlementFundsClaimMachine>
}) {
  const receipt = useSelector(claimMachine, (state) => state.context.receipt)
  const claimState = useSelector(
    claimMachine,
    (state) => state,
    (a, b) => {
      return a.value === b.value
    }
  )
  const project = receipt.project
  const featuredToken = project.featured_token?.[0]
  const amount = receipt.excess_settlement_funds
  const amountInEth = amount ? Number(formatEther(BigInt(amount))).toFixed(4) : '--'

  return (
    <div className="rounded-xs border-outline-variant flex items-center border-b last:border-b-0">
      <div className="bg-surface-low p-xs">
        <ImageWithCustomLoader
          src={featuredToken?.media_url ?? ''}
          alt={`Featured token for project ${project.name}`}
          placeholder={'empty'}
          width={90}
          height={90}
          className="shadow-artwork"
        />
      </div>
      <div className="p-sm flex flex-1">
        <div className="gap-xs p flex flex-1 flex-col">
          <DetailText>
            <span className="text-on-surface-variant font-medium uppercase">{getVerticalTitle(project)}</span>
          </DetailText>
          <BodyText className="font-medium">{project.name}</BodyText>
          <BodyText>
            Settlement amount: <span className="font-medium">{amountInEth} Ξ</span>
          </BodyText>
        </div>
        <div className="flex items-center justify-center">
          <Button
            colorTheme={'outline'}
            disabled={!claimState.matches('idle')}
            onClick={() => {
              claimMachine.send({ type: 'INITIATE_CLAIM' })
            }}
            className="relative h-10 w-20"
          >
            <AnimatePresence>
              <motion.span
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="px-sm py-xs absolute inset-0 flex items-center justify-center"
                key={claimState.value}
              >
                {(() => {
                  if (claimState.matches('idle')) {
                    return <span>Claim</span>
                  }

                  if (claimState.matches('success')) {
                    return <CheckIcon className="w-sm h-sm" />
                  }

                  return <LoadingIndicator className="w-sm h-sm" />
                })()}
              </motion.span>
            </AnimatePresence>
          </Button>
        </div>
      </div>
    </div>
  )
}
